import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { useEffect, useRef, useState } from 'react';
// @mui
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    Container,
    Divider,
    Grid,
    Paper,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TablePagination,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

// components
import { Page } from '../../../components';
import Scrollbar from '../../../components/scrollbar';
import call from '../../../core/services/http';
import { AuditTableHead, TableNoData } from '../../../sections/@dashboard/user';
import { fCurrency, fNumber } from '../../../utils/formatNumber';
import { getAuditDetailItems } from '../store/auditActions';

// ----------------------------------------------------------------------

export default function AuditDetailView() {
  const { id } = useParams();
  console.log('AUDIT ID:', id);
  const dispatch = useDispatch();
  const ref = useRef();
  const { auditRecordItems, auditRecord } = useSelector((state) => state.auditReducer);
  console.log('AUDIT RECORD:', auditRecord);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [barcodeNumber, setBarcodeNumber] = useState('');
  const [orderBy, setOrderBy] = useState('name');
  const [selected, setSelected] = useState([]);
  const [order, setOrder] = useState('asc');
  const [statusFilter, setStatusFilter] = useState('audited');
  const [scannedItems, setScannedItems] = useState([]);
  const [showTable, setShowTable] = useState(true);

  const toastrSuccess = (msg) => {
    toast.success(msg);
  };
  const toastrError = (msg) => {
    toast.error(msg);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleSort = (event, id) => {
    const isAsc = orderBy === id && order === 'asc';
    if (id !== '') {
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(id);
    }
  };

  // Filter assets based on status
  const getFilteredAssets = () => {
    if (!showTable || !auditRecordItems) return [];

    const scannedBarcodes = new Set(scannedItems.map((item) => item.asset?.barcode_number));

    switch (statusFilter) {
      case 'audited':
        return auditRecordItems.filter(
          (item) => item.status === 'audited' || scannedBarcodes.has(item.asset?.barcode_number)
        );
      case 'not_audited':
        return auditRecordItems.filter(
          (item) => item.status !== 'audited' && !scannedBarcodes.has(item.asset?.barcode_number)
        );
      default:
        return auditRecordItems;
    }
  };

  const displayedAssets = getFilteredAssets();

  const handleStatusFilterChange = (event, newValue) => {
    setStatusFilter(newValue);
    setPage(0); // Reset to first page when filter changes
  };

  // Handle scanning an asset
  const handleScanAsset = async () => {
    if (auditRecord?.status === 'completed') {
      toastrError('Audit was marked completed');
      return;
    }
    if (!barcodeNumber.trim()) {
      toastrError('Please enter a barcode number');
      return;
    }

    // Show table when first scan is attempted
    if (!showTable) {
      setShowTable(true);
    }

    try {
      // Find the asset in auditRecordItems
      const scannedAsset = auditRecordItems?.find(
        (item) => item.asset?.barcode_number === barcodeNumber || item.asset?.serial_number === barcodeNumber
      );

      if (scannedAsset) {
        // Check if already scanned
        const alreadyScanned = scannedItems.some(
          (item) => item.asset?.barcode_number === barcodeNumber || item.asset?.serial_number === barcodeNumber
        );

        if (alreadyScanned) {
          toastrError('Asset already scanned');
        } else {
          // Call API to mark as audited
          const response = await call('post', `audits/${id}/save-audited/`, { item_id: scannedAsset.id });
          if (response.data.status) {
            // Add to scanned items
            setScannedItems((prev) => [...prev, { ...scannedAsset, status: 'audited' }]);
            toastrSuccess('Asset scanned successfully');

            // Refresh audit detail items to get updated status
            dispatch(getAuditDetailItems({ id, page: 1, rowsPerPage: 1000 }));
          } else {
            toastrError('Failed to scan asset');
          }
        }
      } else {
        toastrError('Asset not found in audit list');
      }
    } catch (error) {
      console.error('Error scanning asset:', error);
      toastrError('Error scanning asset - please try again');
    }

    // Clear the barcode input
    setBarcodeNumber('');
    setSelected([]);
  };

  const handleCompleteAudit = async () => {
    if (!id) {
      toastrError('Audit ID is required');
      return;
    }
    const response = await call('post', `audits/${id}/complete/`);
    if (response.data.status) {
      dispatch(getAuditDetailItems({ id, page: 1, rowsPerPage: 1000 }));
      toastrSuccess(response.data.message);
    } else {
      toastrError('Failed to audit assets');
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleScanAsset();
    }
  };

  // Get counts for tabs
  const auditedCount =
    auditRecordItems?.filter(
      (item) =>
        item.status === 'audited' ||
        scannedItems.some((scanned) => scanned.asset?.barcode_number === item.asset?.barcode_number)
    ).length || 0;

  const notAuditedCount = (auditRecordItems?.length || 0) - auditedCount;

  useEffect(() => {
    dispatch(getAuditDetailItems({ id, page: 1, rowsPerPage: 1000 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id]);

  return (
    <>
      <Page title="Asset Audit">
        <Container maxWidth="xl">
          <Grid container spacing={3}>
            <>
              <Grid item xs={12} md={12} lg={12}>
                <Paper sx={{ py: 3 }} elevation="1">
                  <Stack direction="row" divider={<Divider orientation="vertical" flexItem />}>
                    <Stack width={1} textAlign="center">
                      <Typography variant="subtitle">TOTAL ASSETS</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {fNumber(auditRecordItems?.length || 0)}
                      </Typography>
                    </Stack>

                    <Stack width={1} textAlign="center">
                      <Typography variant="subtitle">AUDITED ASSETS</Typography>
                      <Typography variant="body2" sx={{ color: 'success.main' }}>
                        {fNumber(auditRecord?.total_scanned || auditedCount || 0)}
                      </Typography>
                    </Stack>

                    <Stack width={1} textAlign="center">
                      <Typography variant="subtitle">NOT AUDITED</Typography>
                      <Typography variant="body2" sx={{ color: 'warning.main' }}>
                        {fNumber(auditRecord?.total_unscanned || notAuditedCount || 0)}
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} md={12} lg={12}>
                <Card>
                  <CardHeader title={'Asset Scanner'} subheader={'Scan asset barcodes to locate and verify items'} />
                  <Paper elevation={3} sx={{ p: 2, textAlign: 'center' }}>
                    <Box sx={{ mb: 1 }}>
                      <QrCodeScannerIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                    </Box>
                    <TextField
                      fullWidth
                      size="large"
                      name="barcodeNumber"
                      color="primary"
                      placeholder="Scan asset barcode here or type manually..."
                      ref={ref}
                      value={barcodeNumber}
                      onChange={(event) => setBarcodeNumber(event.target.value.replace(/\s+/g, ''))}
                      onKeyDown={handleKeyDown}
                      focused
                      autoFocus
                    />
                  </Paper>
                </Card>
              </Grid>

              {showTable && (
                <Grid item xs={12} md={12} lg={12}>
                  <Card>
                    <Box sx={{ p: 2 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" component="div">
                          Asset Audit Results
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={handleCompleteAudit}
                            disabled={auditRecord?.status === 'pending' || auditRecord?.status === 'completed'}
                          >
                            Mark as complete
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                      <Tabs value={statusFilter} onChange={handleStatusFilterChange}>
                        <Tab label={`Scanned (${auditRecord?.total_scanned || auditedCount || 0})`} value="audited" />
                        <Tab
                          label={`Not Scanned (${auditRecord?.total_unscanned || notAuditedCount || 0})`}
                          value="not_audited"
                        />
                      </Tabs>
                    </Box>
                    <CardHeader
                      title={`${statusFilter === 'audited' ? 'Scanned' : 'Not Scanned'} Assets`}
                      subheader={
                        statusFilter === 'audited'
                          ? 'Assets successfully scanned and verified'
                          : 'Assets awaiting audit verification'
                      }
                    />
                    <CardContent>
                      <Scrollbar>
                        <TableContainer sx={{ minWidth: 800 }} component={Paper}>
                          <Table size="small">
                            <AuditTableHead
                              order={order}
                              orderBy={orderBy}
                              rowCount={displayedAssets?.length}
                              numSelected={selected?.length}
                              onRequestSort={handleSort}
                              headLabel={[
                                { id: 'name', label: 'Description', alignRight: false },
                                { id: 'asset_class', label: 'Category', alignRight: false },
                                { id: 'barcode_number', label: 'Barcode#', alignRight: false },
                                { id: 'serial_no', label: 'Serial#', alignRight: false },
                                { id: 'cost', label: 'Cost', alignRight: false },
                                { id: 'status', label: 'Status', alignRight: false },
                                { id: 'location', label: 'Current Location', alignRight: false },
                                { id: 'assigned_to', label: 'AssignedTo', alignRight: false },
                                { id: 'audited', label: 'Audited', alignRight: false },
                              ]}
                            />
                            <TableBody>
                              {displayedAssets?.length > 0 ? (
                                displayedAssets
                                  ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                  ?.map((row, index) => {
                                    const asset = row?.asset;
                                    const isAudited =
                                      row?.status === 'audited' ||
                                      scannedItems.some(
                                        (scanned) => scanned.asset?.barcode_number === asset?.barcode_number
                                      );

                                    return (
                                      <TableRow hover key={row?.id || index} tabIndex={-1} role="checkbox">
                                        <TableCell padding="checkbox">
                                          <Checkbox checked={isAudited} disabled color="primary" />
                                        </TableCell>
                                        <TableCell align="left">{asset?.name}</TableCell>
                                        <TableCell align="left">{asset?.category?.name}</TableCell>
                                        <TableCell align="left">{asset?.barcode_number}</TableCell>
                                        <TableCell align="left">{asset?.serial_number}</TableCell>
                                        <TableCell align="left">{fCurrency(asset?.purchase_cost)}</TableCell>
                                        <TableCell align="left">{asset?.status}</TableCell>
                                        <TableCell align="left">{asset?.location?.name}</TableCell>
                                        <TableCell align="left">{asset?.custodian?.display_name}</TableCell>
                                        <TableCell align="left">
                                          <Typography
                                            variant="body2"
                                            color={isAudited ? 'success.dark' : 'warning.dark'}
                                          >
                                            {isAudited ? 'Audited' : 'Not Audited'}
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })
                              ) : (
                                <TableNoData isNotFound />
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Scrollbar>

                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        component="div"
                        count={displayedAssets?.length || 0}
                        rowsPerPage={rowsPerPage}
                        page={Math.min(page, Math.max(0, Math.ceil((displayedAssets?.length || 0) / rowsPerPage) - 1))}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </>
          </Grid>
        </Container>
      </Page>
    </>
  );
}
